import { TolgeeProvider, TranslationCacheForegroundRefresh } from '@aikoa/i18n/react'
import {
  Figtree_300Light,
  Figtree_400Regular,
  Figtree_500Medium,
  Figtree_600SemiBold,
  Figtree_700Bold,
} from '@expo-google-fonts/figtree'
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet'
import NetInfo from '@react-native-community/netinfo'
import { DefaultTheme, ThemeProvider, type Theme } from '@react-navigation/native'
import { PortalHost } from '@rn-primitives/portal'
import { focusManager, onlineManager, QueryClientProvider } from '@tanstack/react-query'
import { useFonts } from 'expo-font'
import { SplashScreen, Stack } from 'expo-router'
import { StatusBar } from 'expo-status-bar'
import { useEffect } from 'react'
import { AppState, Platform, type AppStateStatus } from 'react-native'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { AlertProvider } from '~/components/GlobalAlert'
import { LanguageChangeOverlay } from '~/components/i18n/LanguageChangeOverlay'
import { LanguageSyncProvider } from '~/components/i18n/LanguageSyncProvider'
import AppViewContextProvider from '~/components/layout/AppViewContext'
import { LayoutWrapper } from '~/components/layout/LayoutWrapper'
import { ScrollContextProvider } from '~/components/layout/ScrollContext'
import { LoadingView } from '~/components/LoadingView'
import { SplashScreenController } from '~/components/Splash'
import { SessionProvider, useSession } from '~/components/user/SessionProvider'
import { env } from '~/env'
import '~/global.css'
import { PostHogProvider } from '~/lib/analytics/posthog'
import PostHogPageView from '~/lib/analytics/PostHogPageView'
import ConfettiProvider from '~/lib/ConfettiProvider'
import { NAV_THEME } from '~/lib/constants'
import { getStackHeaderOptions } from '~/lib/headerConfig'
import { queryClient } from '~/lib/trpc'

void SplashScreen.preventAutoHideAsync()

export const unstable_settings = {
  initialRouteName: '/(front)',
}

if (__DEV__ && Platform.OS !== 'web') {
  require('../../ReactotronConfig')
}

if (Platform.OS !== 'web') {
  onlineManager.setEventListener((setOnline) => {
    return NetInfo.addEventListener((state) => {
      setOnline(!!state.isConnected)
    })
  })
}

function onAppStateChange(status: AppStateStatus) {
  if (Platform.OS !== 'web') {
    focusManager.setFocused(status === 'active')
  }
}

const LIGHT_THEME: Theme = {
  ...DefaultTheme,
  colors: { ...NAV_THEME.light, background: 'transparent' },
}

const tolgeeConfig = {
  apiUrl: env.EXPO_PUBLIC_TOLGEE_API_URL,
  apiKey: env.EXPO_PUBLIC_TOLGEE_API_KEY,
  cdnUrl: env.EXPO_PUBLIC_TOLGEE_CDN_URL,
  cacheKey: env.EXPO_PUBLIC_TRANSLATIONS_BUILD_ID,
}

export default function RootLayout() {
  const [_, error] = useFonts({
    Figtree_300Light,
    Figtree_400Regular,
    Figtree_500Medium,
    Figtree_600SemiBold,
    Figtree_700Bold,
  })

  if (error) {
    console.log(error)
  }

  useEffect(() => {
    if (Platform.OS !== 'web') {
      const subscription = AppState.addEventListener('change', onAppStateChange)
      return () => subscription.remove()
    }
  }, [])

  return (
    <PostHogProvider>
      <QueryClientProvider client={queryClient}>
        <SafeAreaProvider>
          <GestureHandlerRootView className="min-h-0 flex-1">
            <PostHogPageView />
            <ThemeProvider value={LIGHT_THEME}>
              <AppViewContextProvider backgroundTheme={0}>
                <SessionProvider>
                  <SplashScreenController />
                  <TolgeeProvider config={tolgeeConfig}>
                    <TranslationCacheForegroundRefresh />
                    <LanguageSyncProvider>
                      <LanguageChangeOverlay />
                      <ScrollContextProvider>
                        <ConfettiProvider>
                          <AlertProvider>
                            <BottomSheetModalProvider>
                              <StatusBar style="dark" />
                              <AppWrapper />
                            </BottomSheetModalProvider>
                            <PortalHost />
                          </AlertProvider>
                        </ConfettiProvider>
                      </ScrollContextProvider>
                    </LanguageSyncProvider>
                  </TolgeeProvider>
                </SessionProvider>
              </AppViewContextProvider>
            </ThemeProvider>
          </GestureHandlerRootView>
        </SafeAreaProvider>
      </QueryClientProvider>
    </PostHogProvider>
  )
}

function AppWrapper() {
  const { session, isAnonymous, isLoading: isLoadingSession } = useSession()
  const isAuthenticated = !!session?.user && !isAnonymous

  if (isLoadingSession) {
    return <LoadingView />
  }

  return (
    <LayoutWrapper>
      <Stack screenOptions={getStackHeaderOptions()}>
        <Stack.Protected guard={isAuthenticated}>
          <Stack.Screen name="(authenticated)" />
        </Stack.Protected>
        <Stack.Screen name="(front)" />
        <Stack.Screen name="(public_preview)" />
      </Stack>
    </LayoutWrapper>
  )
}
